import { createBrowserRouter, Navigate } from 'react-router-dom'
import { SignInPage, ForgotPasswordPage, ResetPasswordPage, NoProjectsPage, ProtectedRoute } from '@/features/auth'
import { ProjectSwitcherPage, OverviewPage } from '@/features/projects'
import { AppShell } from '@/components/shared/app-shell'
import { AccountShell } from '@/components/shared/account-shell'
import { RecordsWorkspacePage } from '@/features/records/routes/records-workspace-page'
import { RecordDetailPage } from '@/features/records/routes/record-detail-page'
import { FormSubmissionPage } from '@/features/records/routes/form-submission-page'
import { FormInstancesPage } from '@/features/records/routes/form-instances-page'
import { MembersPage } from '@/features/members/routes/members-page'
import { FormBuilderHome } from '@/features/form-builder/routes/form-builder-home'
import { FormEditorPage } from '@/features/form-builder/routes/form-editor-page'
import { FormPreviewPage } from '@/features/form-builder/routes/form-preview-page'
import { PendingReviewPage } from '@/features/review/routes/pending-review-page'
import { DataExplorerPage } from '@/features/data-explorer/routes/data-explorer-page'
import { DataIoPage } from '@/features/data-io/routes/data-io-page'
import { DataExportPage } from '@/features/data-io/routes/data-export-page'
import { DataImportPage } from '@/features/data-io/routes/data-import-page'
import { SettingsPage } from '@/features/settings/routes/settings-page'
import { AuditLogPage } from '@/features/audit/routes/audit-log-page'
import { NotificationsPage } from '@/features/notifications/routes/notifications-page'
import { ProfilePage } from '@/features/account/routes/profile-page'
import { NotFoundPage } from '@/components/shared/error-pages'
import { AdminShell } from '@/features/admin/components/admin-shell'
import { SuperUserGuard } from '@/features/admin/components/super-user-guard'
import { PlatformProjectsPage } from '@/features/admin/routes/platform-projects-page'
import { PlatformUsersPage } from '@/features/admin/routes/platform-users-page'
import { PublicFormFillPage } from '@/features/public-links/routes/public-form-fill-page'

export const router = createBrowserRouter([
  // Public
  { path: '/sign-in', element: <SignInPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  // Anonymous public form fill — no auth, no project context
  { path: '/p/:token', element: <PublicFormFillPage /> },

  // Root
  { path: '/', element: <Navigate to="/projects" replace /> },

  // Protected
  {
    element: <ProtectedRoute />,
    children: [
      { path: '/projects', element: <ProjectSwitcherPage /> },
      { path: '/no-projects', element: <NoProjectsPage /> },

      // Account (no project context)
      {
        path: '/account',
        element: <AccountShell />,
        children: [{ index: true, element: <ProfilePage /> }],
      },

      // Project workspace
      {
        path: '/projects/:projectId/overview',
        element: <AppShell activeNavKey="overview" />,
        children: [{ index: true, element: <OverviewPage /> }],
      },
      {
        path: '/projects/:projectId/records',
        element: <AppShell activeNavKey="records" />,
        children: [
          // Workspace: single-page split-screen (?record=&form=&submission=)
          { index: true, element: <RecordsWorkspacePage /> },
          // Legacy deep-link routes kept for backward compatibility
          { path: ':recordId', element: <RecordDetailPage /> },
          { path: ':recordId/forms/:formId', element: <FormSubmissionPage /> },
          { path: ':recordId/forms/:formId/new', element: <FormSubmissionPage /> },
          { path: ':recordId/forms/:formId/instances', element: <FormInstancesPage /> },
        ],
      },
      {
        path: '/projects/:projectId/pending-review',
        element: <AppShell activeNavKey="pending" />,
        children: [{ index: true, element: <PendingReviewPage /> }],
      },
      {
        path: '/projects/:projectId/explore',
        element: <AppShell activeNavKey="explore" />,
        children: [{ index: true, element: <DataExplorerPage /> }],
      },
      {
        path: '/projects/:projectId/data-io',
        element: <AppShell activeNavKey="data-io" />,
        children: [
          { index: true, element: <DataIoPage /> },
          { path: 'export', element: <DataExportPage /> },
          { path: 'import', element: <DataImportPage /> },
        ],
      },
      {
        path: '/projects/:projectId/form-builder',
        element: <AppShell activeNavKey="form-builder" />,
        children: [
          { index: true, element: <FormBuilderHome /> },
          { path: ':formId', element: <FormEditorPage /> },
          { path: ':formId/preview', element: <FormPreviewPage /> },
        ],
      },
      { path: '/projects/:projectId/summary-card', element: <Navigate to="../settings" replace /> },
      { path: '/projects/:projectId/searchable-fields', element: <Navigate to="../settings" replace /> },
      { path: '/projects/:projectId/permissions', element: <Navigate to="../settings" replace /> },
      {
        path: '/projects/:projectId/members',
        element: <AppShell activeNavKey="members" />,
        children: [{ index: true, element: <MembersPage /> }],
      },
      {
        path: '/projects/:projectId/settings',
        element: <AppShell activeNavKey="settings" />,
        children: [{ index: true, element: <SettingsPage /> }],
      },
      {
        path: '/projects/:projectId/audit',
        element: <AppShell activeNavKey="audit" />,
        children: [{ index: true, element: <AuditLogPage /> }],
      },
      {
        path: '/projects/:projectId/notifications',
        element: <AppShell activeNavKey="notifications" />,
        children: [{ index: true, element: <NotificationsPage /> }],
      },

      // Admin panel (super user only)
      {
        element: <SuperUserGuard />,
        children: [
          {
            path: '/admin',
            element: <AdminShell />,
            children: [
              { index: true, element: <Navigate to="/admin/projects" replace /> },
              { path: 'projects', element: <PlatformProjectsPage /> },
              { path: 'users', element: <PlatformUsersPage /> },
            ],
          },
        ],
      },
    ],
  },

  // Catch-all 404
  { path: '*', element: <NotFoundPage /> },
])
